/**
 * Frontend mirror of the payment service's entitlement-product registry
 * (services/payment/src/lib/entitlement-products.ts).
 *
 * BASE_ACCESS_FEATURES is the EXACT-MATCH allowlist of `user_entitlements.feature`
 * values that bundle full JDS Unlimited access during the exam-prep period.
 *
 * SECURITY — exact match ONLY. Any access check against this list must use an
 * exact-equality / `.in()` match, NEVER a prefix / `startsWith` / `like` match.
 * A prefix match would also catch cycle add-ons (`bar_package_next_cycle`,
 * `*_next_cycle`) and guarantee grants (`guarantee_grant`), which grant
 * NEXT-CYCLE access — NOT base access — and would leak free Unlimited access.
 *
 * Keep this in sync with the backend registry. The two are intentionally
 * duplicated (the payment service is a separate build with no shared import path
 * into `src/`); the list is short and security-sensitive enough that explicit
 * duplication is safer than a fragile cross-package import.
 */

export type ExamPackageFeature = 'bar_package' | 'ube_package';

/**
 * Provisioned (non-purchase) base-access feature for institution pilots — the
 * 314 Formative Assessment Kit (spec D8). Granted per-student by JDS for the
 * pilot window (expires_at = pilot end); bundles JDS Unlimited while active.
 * The faculty-side 'faculty_console' feature gates the Professor Console only
 * and is deliberately NOT base access.
 */
export const INSTITUTION_ACCESS_FEATURE = 'institution_access' as const;

export type BaseAccessFeature = ExamPackageFeature | typeof INSTITUTION_ACCESS_FEATURE;

/**
 * Exact-match allowlist of BASE-access exam-package features. Holding an active
 * `user_entitlements` row for one of these grants bundled JDS Unlimited access.
 *
 * This list MUST remain exactly the base packages. Extended Access next-cycle
 * add-ons (feature 'bar_package_next_cycle' / any '*_next_cycle') and guarantee
 * grants ('guarantee_grant') MUST NOT appear here — they are scheduling/credit
 * tokens that roll the base row forward, not access grants in themselves (see the
 * load-time assertion below + the backend registry's ADDON_PRODUCTS separation in
 * services/payment/src/lib/entitlement-products.ts).
 */
export const BASE_ACCESS_FEATURES: BaseAccessFeature[] = [
  'bar_package',
  'ube_package',
  INSTITUTION_ACCESS_FEATURE,
];

/**
 * The user_entitlements.feature value for Extended Access next-cycle add-ons.
 * Intentionally a SEPARATE constant from BASE_ACCESS_FEATURES — surfaces that
 * gate access read BASE_ACCESS_FEATURES (and the base `bar_package` row); the
 * add-on row is never the access-granting row. Exposed so add-on-aware UI (e.g.
 * the post-checkout upsell or an "Extended Access added" badge) can reference it
 * without re-deriving the string.
 */
export const ADDON_NEXT_CYCLE_FEATURE = 'bar_package_next_cycle' as const;

/** Is this feature one of the base-access exam packages? */
export function isExamPackageFeature(feature: string | null | undefined): feature is ExamPackageFeature {
  return feature === 'bar_package' || feature === 'ube_package';
}

// Load-time security tripwire (mirrors the backend assertion). If a future edit
// ever lets a next-cycle add-on or guarantee grant into BASE_ACCESS_FEATURES, the
// frontend bundle fails to evaluate this module rather than silently granting free
// Unlimited access via the bundling fallback.
for (const feature of BASE_ACCESS_FEATURES as string[]) {
  if (feature.endsWith('_next_cycle') || feature === 'guarantee_grant') {
    throw new Error(
      `SECURITY: BASE_ACCESS_FEATURES must never contain a next-cycle add-on or ` +
        `guarantee grant — found '${feature}'.`
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Human-facing display labels
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Human-facing product names for `user_entitlements.feature` keys. Mirrors the
 * product naming in the backend registry (services/payment/src/lib/entitlement-products.ts:
 * bar_package → NextGen Bar Prep, ube_package → classic UBE) but lives here because
 * the payment service is a separate build with no import path into `src/`.
 *
 * Used by admin / CRM surfaces to humanize raw entitlement keys. Add a label here
 * (not inline) whenever a new entitlement feature needs to render to a person.
 */
export const ENTITLEMENT_FEATURE_LABELS: Record<string, string> = {
  bar_package: 'NextGen Bar Prep',
  ube_package: 'Legacy Bar Prep',
  bar_package_next_cycle: 'NextGen Extended Access',
  guarantee_grant: 'Guarantee Grant',
  institution_access: 'Institution Pilot Access',
  faculty_console: 'Faculty Console',
};

/** Human-facing product name for an entitlement feature key (Title-Cases unknowns). */
export function entitlementFeatureLabel(feature: string | null | undefined): string {
  if (!feature) return 'Unknown';
  return (
    ENTITLEMENT_FEATURE_LABELS[feature] ??
    feature.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * Format an exam-cycle string (`YYYY-MM`, e.g. "2026-07") as a human-readable
 * "Mon YYYY" (e.g. "Jul 2026"). Returns the raw value unchanged if it isn't in
 * the expected `YYYY-MM` shape, so it's safe to call on any cycle string.
 */
export function formatExamCycle(cycle: string | null | undefined): string | null {
  if (!cycle) return null;
  const m = /^(\d{4})-(\d{2})$/.exec(cycle.trim());
  if (!m) return cycle;
  const year = m[1];
  const monthIdx = Number(m[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return cycle;
  return `${MONTH_ABBR[monthIdx]} ${year}`;
}
